import { useEffect, useState, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import { Users, ArrowLeft, RefreshCw, Trash2, ShieldCheck, Search, Loader2, Pencil, Crown, Star } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function fmt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function AdminUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [dialog, setDialog] = useState(null); // { kind, target }
  const [confirmText, setConfirmText] = useState("");
  const [edit, setEdit] = useState(null); // { id, name, role, is_premium }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, s] = await Promise.all([api.get("/admin/users"), api.get("/admin/stats").catch(() => ({ data: null }))]);
      setUsers(Array.isArray(u.data) ? u.data : []);
      setStats(s.data);
    } catch {
      toast.error("Failed to load users.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user?.role === "owner") load(); }, [user, load]);

  if (user && user.role !== "owner") return <Navigate to="/home" replace />;

  const filtered = q
    ? users.filter((u) => (u.email || "").toLowerCase().includes(q.toLowerCase()) || (u.name || "").toLowerCase().includes(q.toLowerCase()))
    : users;

  const nonAdminCount = users.filter((u) => !u.is_admin).length;
  const selectableIds = filtered.filter((u) => u.id !== user?.id).map((u) => u.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const someSelected = selected.size > 0 && !allSelected;
  const toggleOne = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(() => (allSelected ? new Set() : new Set(selectableIds)));

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/users/${edit.id}`, { name: edit.name, role: edit.role, is_premium: edit.is_premium });
      toast.success("User updated.");
      setEdit(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doReset = async (target) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/users/${target.id}/reset`);
      toast.success(`Reset — ${r.data?.purged_total || 0} rows purged.`);
      setDialog(null); setConfirmText(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doDelete = async (target, confirmation) => {
    setBusy(true);
    try {
      const r = await api.delete(`/admin/users/${target.id}`, { data: { confirmation } });
      toast.success(`Deleted ${r.data?.deleted} — ${r.data?.purged_total || 0} rows purged.`);
      setDialog(null); setConfirmText(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doBulkDelete = async () => {
    const ids = [...selected];
    setBusy(true);
    try {
      const r = await api.post("/admin/users/bulk-delete", { ids, confirmation: "DELETE" });
      toast.success(`Deleted ${r.data?.deleted_count || 0} user(s).`);
      setDialog(null); setConfirmText(""); setSelected(new Set()); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doPurge = async (confirmation) => {
    setBusy(true);
    try {
      const r = await api.delete("/admin/users/purge-non-admin", { data: { confirmation } });
      toast.success(`Purged ${r.data?.users_deleted || 0} non-admin users.`);
      setDialog(null); setConfirmText(""); setSelected(new Set()); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const StatCard = ({ label, value }) => (
    <div className="bg-white border border-[#E5DED4] rounded-2xl p-5">
      <p className="text-xs uppercase tracking-widest text-[#8A838E]">{label}</p>
      <p className="font-display text-3xl mt-1">{value ?? "—"}</p>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="admin-users-page">
      <Link to="/settings" className="inline-flex items-center gap-2 text-sm text-[#59535B] hover:text-[#291B2D] transition-smooth" data-testid="admin-users-back">
        <ArrowLeft size={14} /> Back to Settings
      </Link>

      <div className="flex items-center gap-3">
        <Users className="text-[#C89B55]" size={22} />
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold">Admin</p>
          <h1 className="font-display text-3xl lg:text-4xl font-semibold">User management</h1>
          <p className="text-sm text-[#59535B] mt-1">Edit members, reset their data, or remove accounts.</p>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Total users" value={stats.total_users} />
          <StatCard label="Members" value={stats.premium_users} />
          <StatCard label="Free" value={stats.free_users} />
          <StatCard label="Community posts" value={stats.posts_total} />
        </div>
      )}

      <div className="bg-white border border-[#E5DED4] rounded-2xl p-4 flex items-center gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A838E]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…"
            data-testid="admin-users-search"
            className="w-full pl-9 pr-3 py-2 rounded-lg ring-1 ring-[#E5DED4] focus:ring-[#291B2D] outline-none text-sm" />
        </div>
        <button onClick={load} data-testid="admin-users-refresh"
          className="px-4 py-2 rounded-lg bg-[#291B2D] text-white text-sm font-medium hover:bg-[#291B2D]/90 transition-smooth flex items-center gap-2">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="bg-white border border-[#E5DED4] rounded-2xl overflow-hidden">
        {selected.size > 0 && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 bg-[#FBF1F1] border-b border-[#E9C0C0]" data-testid="bulk-action-bar">
            <span className="text-sm font-medium" data-testid="bulk-selected-count">{selected.size} selected</span>
            <div className="flex items-center gap-2">
              <button onClick={() => setSelected(new Set())} className="px-3 py-1.5 rounded-lg text-sm text-[#59535B] hover:bg-black/5" data-testid="bulk-clear-btn">Clear</button>
              <button onClick={() => { setDialog({ kind: "bulk-delete" }); setConfirmText(""); }}
                className="px-4 py-1.5 rounded-lg bg-[#9E3A3A] text-white text-sm font-medium hover:bg-[#7A2E2E] inline-flex items-center gap-2" data-testid="bulk-delete-btn">
                <Trash2 size={14} /> Delete {selected.size} selected
              </button>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-black/[0.02] text-[11px] uppercase tracking-widest text-[#8A838E]">
              <tr>
                <th className="px-4 py-3 w-10">
                  <input type="checkbox" checked={allSelected} ref={(el) => { if (el) el.indeterminate = someSelected; }}
                    onChange={toggleAll} className="w-4 h-4 accent-[#9E3A3A] cursor-pointer" aria-label="Select all" data-testid="select-all-checkbox" />
                </th>
                <th className="text-left px-4 py-3">User</th>
                <th className="text-left px-4 py-3">Role</th>
                <th className="text-left px-4 py-3">Membership</th>
                <th className="text-left px-4 py-3">Registered</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="px-4 py-8 text-center text-[#8A838E]"><Loader2 className="inline animate-spin" size={16} /></td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-[#8A838E]">No users match.</td></tr>}
              {filtered.map((u, idx) => {
                const isMe = u.id === user.id;
                return (
                  <tr key={u.id} className={`border-t border-[#E5DED4] hover:bg-black/[0.015] ${selected.has(u.id) ? "bg-[#FBF1F1]/60" : ""}`} data-testid={`admin-user-row-${idx}`}>
                    <td className="px-4 py-3">
                      <input type="checkbox" checked={selected.has(u.id)} disabled={isMe} onChange={() => toggleOne(u.id)}
                        className="w-4 h-4 accent-[#9E3A3A] cursor-pointer disabled:opacity-30" aria-label={`Select ${u.email}`} data-testid={`select-checkbox-${idx}`} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{u.name || "—"}{isMe && <span className="ml-2 text-[10px] text-[#8A838E]">(you)</span>}</div>
                      <div className="text-[11px] text-[#8A838E]">{u.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      {u.is_admin
                        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-[#291B2D] text-white"><Crown size={10} /> Admin</span>
                        : <span className="text-[11px] text-[#59535B]">member</span>}
                    </td>
                    <td className="px-4 py-3">
                      {u.is_premium || u.is_admin
                        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-[#C89B55]/20 text-[#8a6d2f]"><Star size={10} /> Active</span>
                        : <span className="px-2 py-0.5 rounded-full text-[10px] bg-black/5 text-[#59535B]">Free</span>}
                    </td>
                    <td className="px-4 py-3 text-[12px] text-[#59535B]">{fmt(u.created_at)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => setEdit({ id: u.id, name: u.name || "", role: u.role || "member", is_premium: !!u.is_premium })}
                        className="text-[12px] text-[#291B2D] hover:underline mr-3 inline-flex items-center gap-1" data-testid={`edit-btn-${idx}`}>
                        <Pencil size={12} /> Edit
                      </button>
                      <button onClick={() => { setDialog({ kind: "reset", target: u }); setConfirmText(""); }} disabled={u.is_admin}
                        className="text-[12px] text-[#59535B] hover:text-[#291B2D] hover:underline mr-3 disabled:opacity-30" data-testid={`reset-btn-${idx}`}>Reset data</button>
                      <button onClick={() => { setDialog({ kind: "delete", target: u }); setConfirmText(""); }} disabled={isMe}
                        className="text-[12px] text-[#9E3A3A] hover:underline disabled:opacity-30 inline-flex items-center gap-1" data-testid={`delete-btn-${idx}`}>
                        <Trash2 size={12} /> Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Danger zone */}
      <div className="bg-white border border-[#E9C0C0] rounded-2xl p-5" data-testid="admin-users-danger-zone">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-[#FBF1F1] flex items-center justify-center shrink-0"><Trash2 size={16} className="text-[#9E3A3A]" /></div>
          <div className="flex-1">
            <h2 className="font-display text-lg text-[#9E3A3A]">Danger zone</h2>
            <p className="text-[13px] text-[#59535B] mt-1 leading-relaxed">
              Permanently delete <span className="font-semibold">every non-admin user</span> ({nonAdminCount}) and all of their data. Admin accounts are always kept. This cannot be undone.
            </p>
          </div>
          <button onClick={() => { setDialog({ kind: "purge" }); setConfirmText(""); }} disabled={nonAdminCount === 0}
            className="shrink-0 px-4 py-2 rounded-lg bg-[#9E3A3A] text-white text-sm font-medium hover:bg-[#7A2E2E] disabled:opacity-30 inline-flex items-center gap-2" data-testid="purge-non-admin-btn">
            <Trash2 size={14} /> Purge non-admins
          </button>
        </div>
      </div>

      {/* Edit modal */}
      {edit && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => !busy && setEdit(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="edit-user-dialog">
            <h2 className="font-display text-xl">Edit user</h2>
            <label className="block mt-4 text-xs uppercase tracking-widest text-[#8A838E]">Name</label>
            <input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} maxLength={60}
              className="mt-1 w-full px-3 py-2 rounded-lg ring-1 ring-[#E5DED4] focus:ring-[#291B2D] outline-none text-sm" data-testid="edit-name-input" />
            <label className="block mt-4 text-xs uppercase tracking-widest text-[#8A838E]">Role</label>
            <select value={edit.role} onChange={(e) => setEdit({ ...edit, role: e.target.value })} disabled={edit.id === user?.id}
              className="mt-1 w-full px-3 py-2 rounded-lg ring-1 ring-[#E5DED4] focus:ring-[#291B2D] outline-none text-sm bg-white disabled:opacity-50" data-testid="edit-role-select">
              <option value="member">Member</option>
              <option value="owner">Admin (owner)</option>
            </select>
            <label className="mt-4 flex items-center justify-between">
              <span className="text-sm">Active membership</span>
              <input type="checkbox" checked={edit.is_premium} onChange={(e) => setEdit({ ...edit, is_premium: e.target.checked })}
                className="w-5 h-5 accent-[#C89B55] cursor-pointer" data-testid="edit-premium-toggle" />
            </label>
            <div className="mt-6 flex items-center justify-end gap-2">
              <button onClick={() => setEdit(null)} disabled={busy} className="px-4 py-2 rounded-lg text-sm text-[#59535B] hover:bg-black/5" data-testid="edit-cancel">Cancel</button>
              <button onClick={saveEdit} disabled={busy} className="px-4 py-2 rounded-lg bg-[#291B2D] text-white text-sm font-medium hover:bg-[#291B2D]/90 disabled:opacity-50" data-testid="edit-save">
                {busy ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm dialogs */}
      {dialog && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl" data-testid={`confirm-dialog-${dialog.kind}`}>
            {dialog.kind === "reset" && (
              <>
                <h2 className="font-display text-xl">Reset user data?</h2>
                <p className="mt-2 text-sm text-[#59535B]"><span className="font-medium">{dialog.target.name || dialog.target.email}</span> — {dialog.target.email}</p>
                <p className="mt-3 text-[13px] text-[#59535B] leading-relaxed">This wipes their journal, coach chats, progress, check-ins, affirmations, community posts, and notifications. The account itself stays.</p>
                <div className="mt-6 flex items-center justify-end gap-2">
                  <button onClick={() => setDialog(null)} className="px-4 py-2 rounded-lg text-sm text-[#59535B] hover:bg-black/5" data-testid="dialog-cancel">Cancel</button>
                  <button onClick={() => doReset(dialog.target)} disabled={busy} className="px-4 py-2 rounded-lg bg-[#291B2D] text-white text-sm font-medium hover:bg-[#291B2D]/90 disabled:opacity-50" data-testid="confirm-reset-btn">{busy ? "Resetting…" : "Reset data"}</button>
                </div>
              </>
            )}
            {dialog.kind === "delete" && (
              <>
                <h2 className="font-display text-xl text-[#9E3A3A]">Delete this user?</h2>
                <p className="mt-2 text-sm text-[#59535B]"><span className="font-medium">{dialog.target.name || dialog.target.email}</span> — {dialog.target.email}</p>
                <p className="mt-3 text-[13px] text-[#59535B] leading-relaxed">This permanently deletes the user and all their data.</p>
                <p className="mt-4 text-[12px] uppercase tracking-widest text-[#8A838E]">Type <span className="font-mono text-[#9E3A3A]">DELETE</span> or the email to confirm:</p>
                <input autoFocus value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE"
                  className="mt-2 w-full px-3 py-2 rounded-lg ring-1 ring-[#E9C0C0] focus:ring-[#9E3A3A] outline-none text-sm font-mono" data-testid="delete-confirm-input" />
                <div className="mt-6 flex items-center justify-end gap-2">
                  <button onClick={() => setDialog(null)} className="px-4 py-2 rounded-lg text-sm text-[#59535B] hover:bg-black/5" data-testid="dialog-cancel">Cancel</button>
                  <button onClick={() => doDelete(dialog.target, confirmText)} disabled={busy || (confirmText.trim().toLowerCase() !== "delete" && confirmText.trim().toLowerCase() !== (dialog.target.email || "").toLowerCase())}
                    className="px-4 py-2 rounded-lg bg-[#9E3A3A] text-white text-sm font-medium hover:bg-[#7A2E2E] disabled:opacity-40" data-testid="confirm-delete-btn">{busy ? "Deleting…" : "Delete permanently"}</button>
                </div>
              </>
            )}
            {dialog.kind === "bulk-delete" && (
              <>
                <h2 className="font-display text-xl text-[#9E3A3A]">Delete {selected.size} selected user{selected.size === 1 ? "" : "s"}?</h2>
                <p className="mt-2 text-sm text-[#59535B]">This permanently deletes the selected account{selected.size === 1 ? "" : "s"} and all their data. Your own account is always kept.</p>
                <p className="mt-4 text-[12px] uppercase tracking-widest text-[#8A838E]">Type <span className="font-mono text-[#9E3A3A]">DELETE</span> to confirm:</p>
                <input autoFocus value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE"
                  className="mt-2 w-full px-3 py-2 rounded-lg ring-1 ring-[#E9C0C0] focus:ring-[#9E3A3A] outline-none text-sm font-mono" data-testid="bulk-delete-confirm-input" />
                <div className="mt-6 flex items-center justify-end gap-2">
                  <button onClick={() => setDialog(null)} className="px-4 py-2 rounded-lg text-sm text-[#59535B] hover:bg-black/5" data-testid="dialog-cancel">Cancel</button>
                  <button onClick={doBulkDelete} disabled={busy || confirmText.trim().toUpperCase() !== "DELETE"}
                    className="px-4 py-2 rounded-lg bg-[#9E3A3A] text-white text-sm font-medium hover:bg-[#7A2E2E] disabled:opacity-40" data-testid="confirm-bulk-delete-btn">{busy ? "Deleting…" : `Delete ${selected.size}`}</button>
                </div>
              </>
            )}
            {dialog.kind === "purge" && (
              <>
                <h2 className="font-display text-xl text-[#9E3A3A]">Purge ALL non-admin users?</h2>
                <p className="mt-2 text-sm text-[#59535B]">This permanently deletes <span className="font-semibold">{nonAdminCount}</span> user{nonAdminCount === 1 ? "" : "s"} and all their data. Admin accounts are preserved.</p>
                <p className="mt-4 text-[12px] uppercase tracking-widest text-[#8A838E]">Type <span className="font-mono text-[#9E3A3A]">PURGE</span> to confirm:</p>
                <input autoFocus value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="PURGE"
                  className="mt-2 w-full px-3 py-2 rounded-lg ring-1 ring-[#E9C0C0] focus:ring-[#9E3A3A] outline-none text-sm font-mono" data-testid="purge-confirm-input" />
                <div className="mt-6 flex items-center justify-end gap-2">
                  <button onClick={() => setDialog(null)} className="px-4 py-2 rounded-lg text-sm text-[#59535B] hover:bg-black/5" data-testid="dialog-cancel">Cancel</button>
                  <button onClick={() => doPurge(confirmText)} disabled={busy || confirmText.trim().toUpperCase() !== "PURGE"}
                    className="px-4 py-2 rounded-lg bg-[#9E3A3A] text-white text-sm font-medium hover:bg-[#7A2E2E] disabled:opacity-40" data-testid="confirm-purge-btn">{busy ? "Purging…" : `Purge ${nonAdminCount} users`}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
